import { netService } from './NetService';
import { dungeonService } from './DungeonService';
import { coopDeathHost } from './CoopDeathHost';
import { useInventoryStore } from '../store/inventorySlice';
import { useSpectatorStore } from '../store/spectatorSlice';
import { makeDeathResolution, makeReviveRequest } from '../net/sessionEvents';
import { resetMoveTracking } from '../net/inputSync';
import type { PlayerEntity } from '../entities/PlayerEntity';

const REVIVE_SEED_ID = 'reviver-seed';

/**
 * Local-player death/revive resolution. Runs in every browser for its OWN
 * player: the revive seed lives in the client-local inventory, so the decision
 * (revive vs go down) and the walk-to-revive trigger are resolved here, then
 * relayed to the host authority (CoopDeathHost) — directly when local is host,
 * via a reliable message otherwise.
 */
class CoopDeathClient {
  /** Consume one revive seed from the LOCAL inventory; true if one was spent. */
  private consumeReviveSeedLocally(): boolean {
    const inv = useInventoryStore.getState();
    const count =
      inv.slots.find((s) => s?.itemId === REVIVE_SEED_ID)?.quantity ?? 0;
    if (count <= 0) {
      return false;
    }
    inv.removeItem(REVIVE_SEED_ID, 1);
    return true;
  }

  /** Resolve our OWN death — spend a seed to revive, else go down (spectator). */
  onPlayerDied(playerId: string, cell: { x: number; y: number }): void {
    if (playerId !== dungeonService.localPlayerId) {
      return;
    }
    if (this.consumeReviveSeedLocally()) {
      // The host will respawn a fresh entity (lastInputSeq=0); realign move-seq.
      resetMoveTracking();
      netService.session?.broadcast(
        'reliable',
        makeDeathResolution(playerId, cell, true),
      );
      return;
    }
    useSpectatorStore.getState().setDowned(true);
    netService.session?.broadcast(
      'reliable',
      makeDeathResolution(playerId, cell, false),
    );
  }

  checkReviveAt(player: PlayerEntity, x: number, y: number): void {
    if (player.id !== dungeonService.localPlayerId) {
      return;
    }
    const beacon = dungeonService.locationService
      .getPlacedLocations()
      .find((l) => l.id.startsWith('revive:') && l.x === x && l.y === y);
    if (!beacon) {
      return;
    }
    const deadId = beacon.id.slice('revive:'.length);
    if (deadId === player.id) {
      return;
    }
    if (!this.consumeReviveSeedLocally()) {
      return;
    }
    if (netService.isHost()) {
      coopDeathHost.reviveOnHost(deadId);
    } else {
      netService.session?.broadcast('reliable', makeReviveRequest(deadId));
    }
  }

  reset(): void {
    useSpectatorStore.getState().reset();
  }
}

export const coopDeathClient = new CoopDeathClient();

/** Reset the whole co-op death state (host authority + local resolution). */
export function resetCoopDeath(): void {
  coopDeathHost.reset();
  coopDeathClient.reset();
}
